/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * NumericDisplay - Display numérico simples para qualquer valor
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';

const NumericDisplay = ({
  value = 0,
  unit = '',
  label = '',
  zones = [],
  decimalPlaces = 1,
  showLabel = true,
  showUnit = true,
  size = 'medium', // 'small', 'medium', 'large'
}) => {
  // Encontrar a cor da zona atual
  const currentZoneColor = useMemo(() => {
    if (!zones || zones.length === 0) return '#22c55e';
    const numValue = typeof value === 'number' ? value : 0;
    const zone = zones.find(z => numValue >= z.min_value && numValue <= z.max_value);
    return zone?.color || '#22c55e';
  }, [value, zones]);

  const formattedValue = useMemo(() => {
    if (typeof value === 'number') {
      return value.toFixed(decimalPlaces);
    }
    return '--';
  }, [value, decimalPlaces]);

  // Função para abreviar labels longos
  const abbreviateLabel = (text, maxLen = 14) => {
    if (!text || text.length <= maxLen) return text;
    // Palavras comuns para abreviar
    const abbreviations = {
      'Temperatura': 'Temp.',
      'Ambiente': 'Amb.',
      'Consumo': 'Cons.',
      'Combustível': 'Comb.',
      'Velocidade': 'Vel.',
      'Profundidade': 'Prof.',
      'Pressão': 'Press.',
      'Voltagem': 'Volt.',
      'Corrente': 'Corr.',
    };
    
    let result = text;
    for (const [word, abbr] of Object.entries(abbreviations)) {
      result = result.replace(word, abbr);
      if (result.length <= maxLen) return result;
    }
    
    // Se ainda for muito longo, truncar com ...
    if (result.length > maxLen) {
      return result.substring(0, maxLen - 1) + '…';
    }
    return result;
  };

  // Tamanhos baseados no prop size
  const sizes = {
    small: {
      container: 'min-w-[100px] h-20',
      value: 'text-xl',
      unit: 'text-xs',
      label: 'text-xs',
      padding: 'p-2',
    },
    medium: {
      container: 'min-w-[120px] h-24',
      value: 'text-2xl',
      unit: 'text-sm',
      label: 'text-xs',
      padding: 'p-3',
    },
    large: {
      container: 'min-w-[150px] h-28',
      value: 'text-3xl',
      unit: 'text-base',
      label: 'text-sm',
      padding: 'p-4',
    },
  };

  const currentSize = sizes[size] || sizes.medium;
  const displayLabel = abbreviateLabel(label);

  return (
    <div 
      className={`
        ${currentSize.container} ${currentSize.padding}
        flex flex-col items-center justify-center
        bg-gray-900 rounded-lg border border-gray-700
        relative overflow-hidden
      `}
      title={label} // Tooltip com nome completo
    >
      {/* Efeito de glow no fundo */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{ 
          background: `radial-gradient(circle at center, ${currentZoneColor} 0%, transparent 70%)`,
        }}
      />

      {/* Label com abreviação inteligente */}
      {showLabel && label && (
        <div 
          className={`text-gray-400 ${currentSize.label} font-medium text-center mb-1 relative z-10`}
          title={label}
        >
          {displayLabel}
        </div>
      )}

      {/* Valor principal */}
      <div className="flex items-baseline gap-1 relative z-10">
        <span 
          className={`font-mono font-bold ${currentSize.value}`}
          style={{ 
            color: currentZoneColor,
            textShadow: `0 0 12px ${currentZoneColor}50`,
          }}
        >
          {formattedValue}
        </span>
        {showUnit && unit && (
          <span className={`text-gray-400 ${currentSize.unit}`}>
            {unit}
          </span>
        )}
      </div>

      {/* Indicador de zona (barra inferior) */}
      {zones && zones.length > 0 && (
        <div 
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{ backgroundColor: currentZoneColor }}
        />
      )}
    </div>
  );
};

export default NumericDisplay;
